import { Radio, RadioGroupFormikField } from "@certn/cascadia-react";
import { useField } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const DIRECT_PDF_URL = "DIRECT_PDF_URL";
const CLIENT_PORTAL_URL = "CLIENT_PORTAL_URL";

type ReportUrlType = typeof DIRECT_PDF_URL | typeof CLIENT_PORTAL_URL;

interface ReportUrlTypeFieldProps {
  name?: string;
  isDisabled?: boolean;
  defaultValue?: ReportUrlType;
  /**
   * Disables the PDF URL option and forces the selection to Client Portal URL.
   * The *policy* for when to do this (e.g. a regional restriction) belongs to
   * the caller — this component only implements the mechanism.
   */
  isPdfUrlDisabled?: boolean;
  /** Optional helper note rendered under the field (e.g. why PDF is disabled). */
  note?: string;
}

/**
 * Shared "Report URL type" radio for ATS integration setup/edit steps.
 *
 * By default both options are enabled. Callers may disable the PDF URL option
 * (via `isPdfUrlDisabled`) and attach an explanatory `note`; when disabled the
 * selection is coerced to Client Portal URL so a disabled option can never be
 * submitted. This component is intentionally region-agnostic — any
 * integration-specific restriction is decided by the consumer.
 */
export function ReportUrlTypeField({
  name = "report_url_type",
  isDisabled = false,
  defaultValue = CLIENT_PORTAL_URL,
  isPdfUrlDisabled = false,
  note,
}: ReportUrlTypeFieldProps) {
  const { t } = useTranslation();
  const [field, , { setValue }] = useField(name);

  // Safety net: a disabled option must never submit. Callers should seed
  // Client Portal URL in their initial values when disabling PDF (so this never
  // fires in practice), but if a stored/defaulted DIRECT_PDF_URL slips through,
  // coerce it so the disabled option can't be submitted. The value guard keeps
  // this idempotent, so re-running when setValue's identity changes is harmless.
  useEffect(() => {
    if (isPdfUrlDisabled && field.value !== CLIENT_PORTAL_URL) {
      setValue(CLIENT_PORTAL_URL);
    }
  }, [isPdfUrlDisabled, field.value, setValue]);

  return (
    <RadioGroupFormikField
      label={t("Report URL type")}
      testID="report-url-type"
      name={name}
      orientation="horizontal"
      isRequired
      isDisabled={isDisabled}
      defaultValue={isPdfUrlDisabled ? CLIENT_PORTAL_URL : defaultValue}
      description={note}
    >
      <Radio
        testID="direct-pdf-url"
        value={DIRECT_PDF_URL}
        isDisabled={isPdfUrlDisabled}
      >
        {t("PDF URL")}
      </Radio>
      <Radio testID="client-portal-url" value={CLIENT_PORTAL_URL}>
        {t("Client Portal URL (Authenticated)")}
      </Radio>
    </RadioGroupFormikField>
  );
}
